import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import ObiettiviManager from './ObiettiviManager';

// Dialog priorità/esclusioni per singolo agente.
// 456 -> Obiettivi (con target) + esclusioni; copertura/ssmate -> aree prioritarie semplici + esclusioni.
const AG = {
  '456': { titolo: 'Priorità · AI 456', exKey: 'zone_escluse_456', prioKey: null, obiettivi: true },
  copertura: { titolo: 'Priorità · AI Copertura', exKey: 'zone_escluse_copertura', prioKey: 'priorita_aree_copertura', obiettivi: false },
  ssmate: { titolo: 'Priorità · AI SSMATE', exKey: 'zone_escluse_ssmate', prioKey: 'priorita_aree_ssmate', obiettivi: false },
  gea: { titolo: 'Priorità · AI VF', exKey: 'zone_escluse_gea', prioKey: 'priorita_aree_gea', obiettivi: false },
};

const muted = { color: 'var(--muted)', fontSize: 13 };
const nf = (n) => (n ?? 0).toLocaleString('it-IT');

// Scheda compatta con bordo + ombra + titolo (linea sotto) — stile del design (token)
function Section({ title, hint, accent, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)', padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, borderBottom: '1px solid var(--line)', paddingBottom: 7, marginBottom: 10 }}>
        {accent && <span style={{ width: 8, height: 8, borderRadius: 3, background: accent }} />}
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
        {hint && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const selCompact = { fontSize: 12, padding: '6px 9px', border: '1px solid var(--line)', borderRadius: 9, background: 'var(--surface-2)', color: 'var(--text)' };

function AreaEditor({ color, value, onChange, regioni, province, max }) {
  const [selReg, setSelReg] = useState('');
  const [selProv, setSelProv] = useState('');
  const has = (tipo, nome) => value.some((z) => z.tipo === tipo && z.nome === nome);
  const add = (tipo, nome) => { if (nome && !has(tipo, nome)) onChange([...value, { tipo, nome }]); };
  const remove = (tipo, nome) => onChange(value.filter((z) => !(z.tipo === tipo && z.nome === nome)));
  const pieno = max != null && value.length >= max;
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <select value={selReg} style={selCompact} onChange={(e) => setSelReg(e.target.value)}>
            <option value="">— Regione —</option>
            {regioni.map((r) => <option key={r.nome} value={r.nome} disabled={has('regione', r.nome)}>{r.nome} ({nf(r.count)})</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, padding: '4px 9px' }} disabled={!selReg || pieno} onClick={() => { add('regione', selReg); setSelReg(''); }}>+</button>
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <select value={selProv} style={selCompact} onChange={(e) => setSelProv(e.target.value)}>
            <option value="">— Provincia —</option>
            {province.map((p) => <option key={p.nome} value={p.nome} disabled={has('provincia', p.nome)}>{p.nome} ({nf(p.count)})</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, padding: '4px 9px' }} disabled={!selProv || pieno} onClick={() => { add('provincia', selProv); setSelProv(''); }}>+</button>
        </div>
      </div>
      {pieno && <div style={{ fontSize: 11, color: '#b45309', marginBottom: 8 }}>Massimo {max} aree prioritarie.</div>}
      {value.length === 0 ? (
        <div style={{ ...muted, fontSize: 12 }}>Nessuna area.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 116, overflowY: 'auto' }}>
          {value.map((z) => (
            <span key={`${z.tipo}:${z.nome}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', background: color.bg, color: color.fg, borderRadius: 16, fontSize: 11.5, fontWeight: 600 }}>
              {z.tipo === 'regione' ? '🗺️' : '📍'} {z.nome}
              <button onClick={() => remove(z.tipo, z.nome)} style={{ background: 'none', border: 'none', color: color.fg, cursor: 'pointer', fontWeight: 800, fontSize: 12 }}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AgentPriorityDialog({ agente, open, onClose }) {
  const meta = AG[agente] || AG['456'];
  const [regioni, setRegioni] = useState([]);
  const [province, setProvince] = useState([]);
  const [escluse, setEscluse] = useState([]);
  const [priorita, setPriorita] = useState([]);
  const [orig, setOrig] = useState({ escluse: [], priorita: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/dashboard/aree-counts`).then((r) => { setRegioni(r.data?.regioni || []); setProvince(r.data?.province || []); }).catch(() => {});
    axios.get(`${API}/settings`).then((r) => {
      const e = r.data?.[meta.exKey] || [];
      const p = meta.prioKey ? (r.data?.[meta.prioKey] || []) : [];
      setEscluse(e); setPriorita(p); setOrig({ escluse: e, priorita: p });
    }).catch(() => {});
  }, [meta]);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;
  const dirty = JSON.stringify(escluse) !== JSON.stringify(orig.escluse) || JSON.stringify(priorita) !== JSON.stringify(orig.priorita);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { [meta.exKey]: escluse };
      if (meta.prioKey) payload[meta.prioKey] = priorita;
      await axios.put(`${API}/settings`, payload);
      setOrig({ escluse, priorita });
      toast.success('Priorità salvate');
    } catch { toast.error('Errore salvataggio'); }
    setSaving(false);
  };

  return createPortal(
    <>
      <div className="nt-overlay" onClick={onClose} style={{ zIndex: 12000 }}>
        <div className="nt-detail nt-fume" style={{ width: '96vw', maxWidth: 1040, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
          <div className="nt-detail-head"><span>{meta.titolo}</span><button className="ob-x" onClick={onClose}>✕</button></div>
          <div className="nt-detail-body apd" style={{ background: '#f5f7fb', overflow: 'auto', flex: 1, padding: 12 }}>
            {meta.obiettivi ? (
              <Section title="Aree prioritarie (Obiettivi)" hint="l'agente 456 lavora queste aree fino al target" accent="#22c55e">
                <ObiettiviManager />
              </Section>
            ) : (
              <Section title="Aree prioritarie" hint="lavorate PRIMA dall'agente" accent="#22c55e">
                <AreaEditor color={{ bg: 'rgba(34,197,94,.10)', fg: '#15803d' }} value={priorita} onChange={setPriorita} regioni={regioni} province={province} max={3} />
              </Section>
            )}

            <Section title="Esclusioni" hint="aree SALTATE da questo agente" accent="#ef4444">
              <AreaEditor color={{ bg: 'rgba(239,68,68,.10)', fg: '#b91c1c' }} value={escluse} onChange={setEscluse} regioni={regioni} province={province} />
            </Section>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--line)', boxShadow: '0 -2px 10px rgba(0,0,0,.05)', background: 'var(--surface)' }}>
            <span style={{ ...muted, marginRight: 'auto' }}>{meta.obiettivi ? 'Gli obiettivi si salvano da soli.' : ''}</span>
            {dirty && <button className="btn btn-secondary" onClick={() => { setEscluse(orig.escluse); setPriorita(orig.priorita); }}>Annulla</button>}
            <button className="btn btn-primary" disabled={!dirty || saving} onClick={save}>{saving ? 'Salvataggio…' : (meta.prioKey ? 'Salva priorità ed esclusioni' : 'Salva esclusioni')}</button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
